<template lang="pug">
app-main-content-layout(title='')
  .c-failed-orders-page
    section.c-failed-orders-page__header
      .c-failed-orders-page__header-content
        h1.c-failed-orders-page__heading Failed checkout orders
        p.c-failed-orders-page__subtitle
          | Paid cart orders with unfulfilled lines. Retry only when payment is confirmed.

    section.c-failed-orders-table-section
      CmsListToolbar(
        v-model="searchInput"
        search-placeholder="Search by order ID or buyer email"
        @search="handleSearch"
      )
      el-table(
        :data="orders"
        v-loading="loading"
        style="width: 100%"
        row-key="id"
      )
        el-table-column(type="expand" width="48")
          template(#default="scope")
            .order-lines-expand
              p.order-lines-expand__title Order lines
              el-table(
                :data="scope.row.lines || []"
                size="small"
                style="width: 100%"
              )
                el-table-column(prop="lineType" label="Type" width="100")
                el-table-column(prop="stickerTitle" label="Item" min-width="160")
                el-table-column(prop="quantity" label="Qty" width="70")
                el-table-column(label="Unit" width="90")
                  template(#default="lineScope")
                    span ${{ formatMoney(lineScope.row.unitPrice) }}
                el-table-column(label="Fulfilled" width="100")
                  template(#default="lineScope")
                    span(
                      :class="lineScope.row.fulfilled ? 'text-success' : 'text-danger'"
                    ) {{ lineScope.row.fulfilled ? 'Yes' : 'No' }}

        el-table-column(label="Order" min-width="220")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.id }}
              .text-muted.small {{ formatDateTime(scope.row.createdAt) }}

        el-table-column(label="Buyer" min-width="180")
          template(#default="scope")
            .lh-sm
              .font-medium {{ buyerName(scope.row) }}
              .text-muted.small {{ scope.row.email || '—' }}
              span.crm-badge.crm-badge--gray.mt-1(v-if="scope.row.isAnonymousBuyer") Guest

        el-table-column(label="Total" width="100")
          template(#default="scope")
            span ${{ formatMoney(scope.row.totalAmount) }}

        el-table-column(label="Unfulfilled" width="110")
          template(#default="scope")
            span(
              :class="{ 'text-danger': scope.row.unfulfilledLineCount > 0 }"
            ) {{ scope.row.unfulfilledLineCount }}

        el-table-column(label="Payment" width="120")
          template(#default="scope")
            span.crm-badge(
              :class="scope.row.paymentConfirmed ? 'crm-badge--teal' : 'crm-badge--gray'"
            ) {{ scope.row.paymentConfirmed ? 'Confirmed' : 'Not paid' }}

        el-table-column(label="" width="120" fixed="right")
          template(#default="scope")
            el-button(
              v-if="scope.row.paymentConfirmed"
              type="primary"
              size="small"
              :loading="retryingOrderId === scope.row.id"
              @click.stop="handleRetry(scope.row)"
            )
              fai(type="fal" icon="redo").size-14
              span Retry

      .c-failed-orders-load-more(v-if="orders.length")
        el-button(
          v-if="hasMore"
          :loading="loadingMore"
          @click="loadMore"
        ) Load more
        p.c-failed-orders-end(v-else-if="!loading") End of list

      .c-failed-orders-empty(v-if="!loading && !orders.length")
        p No failed checkout orders match your search.
</template>

<script lang="ts">
import {
  defineComponent, onMounted, ref,
} from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import globalShared from '@/shared';
import CmsListToolbar from '@/components/CmsListToolbar.vue';
import { findFailedCheckoutOrders, retryCheckoutOrder } from '@/graphs/gql';

interface FailedCheckoutLine {
  id: string;
  lineType: string;
  stickerId?: string;
  stickerTitle?: string;
  quantity?: number;
  unitPrice?: number;
  donationCauseId?: string;
  donationAmount?: number;
  fulfilled: boolean;
}

interface FailedCheckoutOrder {
  id: string;
  createdAt: string;
  updatedAt: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  buyerEntity?: string;
  buyerEntityId?: string;
  isAnonymousBuyer?: boolean;
  subtotal?: number;
  totalFee?: number;
  givingWalletCredit?: number;
  totalAmount?: number;
  intentId?: string;
  paymentConfirmed: boolean;
  unfulfilledLineCount: number;
  lines: FailedCheckoutLine[];
}

const PAGE_SIZE = 20;

export default defineComponent({
  name: 'FailedCheckoutOrders',
  components: { CmsListToolbar },
  setup() {
    const { makeGraphRequest, getAuthToken, dateAndTime } = globalShared();

    const orders = ref<FailedCheckoutOrder[]>([]);
    const loading = ref(false);
    const loadingMore = ref(false);
    const hasMore = ref(true);
    const searchInput = ref('');
    const activeSearch = ref<string | null>(null);
    const retryingOrderId = ref<string | null>(null);

    function formatMoney(value: number | null | undefined): string {
      const n = typeof value === 'number' ? value : parseFloat(String(value ?? 0));
      if (Number.isNaN(n)) return '0.00';
      return n.toFixed(2);
    }

    function formatDateTime(value: string): string {
      return dateAndTime(value).join(' ');
    }

    function buyerName(row: FailedCheckoutOrder): string {
      const parts = [row.firstName, row.lastName].filter(Boolean);
      if (parts.length) return parts.join(' ');
      return row.email || 'Unknown buyer';
    }

    async function fetchOrders({ append = false }: { append?: boolean } = {}) {
      if (append) {
        loadingMore.value = true;
      } else {
        loading.value = true;
      }

      try {
        const skip = append ? orders.value.length : 0;
        const response = await makeGraphRequest(
          'query',
          findFailedCheckoutOrders,
          {
            search: activeSearch.value,
            skip,
            take: PAGE_SIZE,
          },
          getAuthToken(),
        );

        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to load orders');
          return;
        }

        const batch: FailedCheckoutOrder[] = response?.data?.findFailedCheckoutOrders || [];

        if (append) {
          orders.value = [...orders.value, ...batch];
        } else {
          orders.value = batch;
        }

        hasMore.value = batch.length === PAGE_SIZE;
      } catch (err: any) {
        console.error(err);
        ElMessage.error(err?.message || 'Failed to load orders');
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    }

    function handleSearch() {
      const trimmed = searchInput.value.trim();
      activeSearch.value = trimmed.length ? trimmed : null;
      fetchOrders();
    }

    function loadMore() {
      if (!hasMore.value || loadingMore.value) return;
      fetchOrders({ append: true });
    }

    async function handleRetry(order: FailedCheckoutOrder) {
      try {
        await ElMessageBox.confirm(
          `Retry fulfillment for order ${order.id}?`,
          'Retry checkout order',
          { type: 'warning', confirmButtonText: 'Retry' },
        );
      } catch {
        return;
      }

      retryingOrderId.value = order.id;
      try {
        const response = await makeGraphRequest(
          'mutation',
          retryCheckoutOrder,
          { checkoutOrderId: order.id },
          getAuthToken(),
        );

        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Retry failed');
          return;
        }

        const fullyFulfilled = response?.data?.retryCheckoutOrder === true;
        if (fullyFulfilled) {
          ElMessage.success('Order fully fulfilled');
        } else {
          ElMessage.warning('Retry completed; some lines may still be unfulfilled');
        }

        await fetchOrders();
      } catch (err: any) {
        console.error(err);
        ElMessage.error(err?.message || 'Retry failed');
      } finally {
        retryingOrderId.value = null;
      }
    }

    onMounted(() => {
      fetchOrders();
    });

    return {
      orders,
      loading,
      loadingMore,
      hasMore,
      searchInput,
      retryingOrderId,
      formatMoney,
      formatDateTime,
      buyerName,
      handleSearch,
      loadMore,
      handleRetry,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../crm-list';

.c-failed-orders-page {
  @include crm-page;
}

@include crm-header('c-failed-orders-page');
@include crm-table('c-failed-orders-table-section');
@include crm-cells;

.c-failed-orders-page__subtitle {
  max-width: 42rem;
}


.c-failed-orders-table-section {
  :deep(.el-table__row) {
    cursor: default;
  }

  :deep(.el-table__expanded-cell) {
    background: $gray-50;
  }
}

.c-failed-orders-load-more {
  display: flex;
  justify-content: center;
  padding: 1rem;
  border-top: 1px solid $border;
}

.c-failed-orders-end {
  text-align: center;
  color: $muted-foreground;
  font-size: 0.875rem;
  margin: 0;
}

.c-failed-orders-empty {
  text-align: center;
  padding: 2rem 1rem;
  font-size: 0.875rem;
  color: $gray-600;

  p {
    margin: 0;
  }
}

.order-lines-expand {
  padding: 0.5rem 1rem 1rem;

  &__title {
    font-size: 0.875rem;
    font-weight: 500;
    margin: 0 0 0.75rem;
    color: $foreground;
  }
}

.text-muted {
  color: $muted-foreground;
}

.small {
  font-size: 0.75rem;
}

.text-success {
  color: $teal-text;
  font-weight: 500;
}

.text-danger {
  color: #d4183d;
  font-weight: 500;
}

.font-medium {
  font-weight: 500;
  color: $foreground;
}

@media (max-width: 768px) {
  .c-failed-orders-page {
    padding: 1.5rem;
  }
}
</style>
